import { useEffect } from "react";
import { useT } from "../i18n";
import { usePreis } from "../daten/haken";
import { alsMerkzuschlag, useMerkliste, zuschlagNetto, type Merkposten } from "../daten/merkliste";
import { aufliegerNochGebraucht, aufliegerWunsch, mitgebracht } from "../daten/abholung";

/* ══════════════════════════════════════════════════════════════════
   „Auflieger mitgeben" — der Schalter an der Sammelkarte (18.09.2026).

   Migration 0072 kennt Gespanne: *„Zugmaschinen-Auflieger-Kombinationen
   kannst du auch mit einbauen"* (Luca, 16.09.2026). Die alte
   Schnellerfassung schlug den Auflieger vor, die Vermietfläche nicht —
   einer der beiden Punkte aus `docs/38-vermietmodul.md`, „Was bis zum
   Umschalten noch fehlt".

   ── Warum er an der Karte steht und nicht in der Trefferzeile ──────

   In der Trefferzeile gibt es noch keinen Posten, an den er sich hängen
   könnte: Dort steht ein Typ, und der Auflieger ist ein **zweites Gerät**
   mit eigener Position und eigener Belegung (Regel 8). Ein Haken an einer
   Zeile, die man vielleicht gar nicht merkt, legt entweder nichts an oder
   zwei Dinge auf einmal — beides ist falsch.

   Und er gehört nicht ins Blatt am Posten, obwohl der Entwurf das einmal
   sagte: Wer über das Nachschlagen arbeitet, sieht die Sammlung nur in der
   Leiste; ein Vorschlag, den man erst durch ein Blatt findet, ist für ihn
   keiner (Luca, 18.09.2026, zum Zeitraum: *„kann ich den Zeitraum dann
   nicht mehr anpassen"*). An der Karte steht er in allen drei Fassungen —
   Spalte, Leiste, Blatt am Telefon. **Eine Karte ist eine Karte.**

   ── Und warum dieselbe Mechanik wie die Selbstabholung ─────────────

   Der Anhänger bei „Kunde holt ab" (0127) ist genau dasselbe: ein zweites
   Gerät, das an einem Posten hängt (`wegen`) und mit ihm wieder geht. Der
   Weg dahin steht deshalb einmal in `daten/abholung.ts` und wird hier
   benutzt, statt daneben noch einmal gebaut zu werden. Unterschieden
   werden die beiden über `wegenArt` — eine Zugmaschine, die der Kunde
   selbst holt, braucht keinen Transportanhänger und trotzdem ihren
   Auflieger.

   **Er ist ein Vorschlag, kein Riegel** (0072): Wer eine Zugmaschine ohne
   Auflieger vermietet, tut nichts Verbotenes. Deshalb steht der Schalter
   aus, bis jemand ihn anmacht, und wer die Zeile von Hand herausnimmt, hat
   sie herausgenommen.

   Klassen: `ml-abholung*` — dieselbe Form wie der Schalter daneben, weil
   es dieselbe Sorte Entscheidung ist.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Der Schalter, wenn es einen Vorschlag gibt — sonst nichts.
 *
 * **Ein mitgebrachter Posten trägt keinen.** Der Auflieger kommt, weil die
 * Zugmaschine kommt; ein zweiter Schalter an ihm wäre ein Widerspruch in
 * einer Liste (`wegen`).
 */
export function Gespann({ p }: { p: Merkposten }) {
  if (!p.gespann || p.wegen) return null;
  return <Schalter p={p} />;
}

function Schalter({ p }: { p: Merkposten }) {
  const t = useT();
  const { posten, wer, merkenWennFrei, aendern, entfernen } = useMerkliste();
  const an = p.mitAuflieger === true;

  /* **Der Preis des Aufliegers — gefragt, solange der Schalter an ist.**
     `usePreis` fragt nur mit einem Typ; ohne Schalter steht dort nichts, und
     die Sammlung kostet keine Abfrage mehr als vorher. */
  const aufliegerPosten = mitgebracht(posten, p, "gespann");
  const auskunft = usePreis(
    an && aufliegerPosten && !aufliegerPosten.gerechnet ? aufliegerPosten.typId : undefined,
    p.von,
    p.bis,
    wer.nr || null,
    false,
    aufliegerPosten?.varianteId || undefined,
  );
  const preis = auskunft.data;

  /* Der Satz steht an der Zeile, sobald die Antwort da ist. Vorher steht dort
     „Preis offen" — eine erfundene Null wäre schlimmer als eine Zeile, die
     sagt, dass sie noch wartet. */
  useEffect(() => {
    if (!preis || !aufliegerPosten || aufliegerPosten.gerechnet) return;
    const gerechnet = preis.zuschlaege.filter((z) => z.vorgewaehlt).map(alsMerkzuschlag);
    aendern(aufliegerPosten.id, {
      gerechnet: true,
      miettage: preis.miettage,
      netto: preis.netto,
      einheiten: preis.menge,
      einheitKey: preis.einheit_key,
      zuschlaege: gerechnet,
      zuschlaegeNetto: gerechnet.reduce((s, z) => s + zuschlagNetto(z), 0),
    });
  }, [preis, aufliegerPosten, aendern]);

  const umlegen = async (neu: boolean) => {
    if (neu) {
      /* **Erst der Auflieger, dann der Schalter** (19.09.2026): Ist er im
         Zeitraum nicht frei, kommt das Fenster, und der Schalter bleibt aus
         — ein „mit Auflieger" ohne Auflieger in der Sammlung wäre eine
         Zusage, die niemand einlösen kann. */
      const wunsch = aufliegerWunsch(p);
      if (wunsch && !(await merkenWennFrei(wunsch))) return;
      aendern(p.id, { mitAuflieger: true });
      return;
    }
    aendern(p.id, { mitAuflieger: undefined });
    /* Zurück heißt zurück — aber nur, solange niemand anders denselben
       Auflieger im selben Fenster noch braucht. Zwei Zugmaschinen teilen
       sich einen Vorschlag; wie viele es wirklich werden, entscheidet die
       Menge an der Zeile, und die setzt ein Mensch (Regel 11). */
    if (aufliegerPosten && !aufliegerNochGebraucht(posten, aufliegerPosten, p.id)) {
      entfernen(aufliegerPosten.id);
    }
  };

  return (
    <div className="ml-abholung">
      <label className="ml-abholung-schalter">
        <input
          type="checkbox"
          checked={an}
          onChange={(e) => { void umlegen(e.target.checked); }}
        />
        <span>{t("merkliste.mitAuflieger", { was: p.gespann?.typ ?? "" })}</span>
      </label>
      {/* **Was der Schalter bewirkt hat, steht daneben** — und zwar der
          Satz, der am Telefon gesagt wird: Der Auflieger ist eine eigene
          Zeile mit eigenem Preis, kein Zubehör am Gerät. */}
      {an ? (
        <span className="ml-abholung-folge">{t("merkliste.mitAufliegerFolge")}</span>
      ) : null}
    </div>
  );
}
